/**
 * Typed failure for the shared client upload pipeline.
 *
 * Every surface that stages a file (brand library, editor, chat, brand
 * setup) gets the same object, so "can this be retried?" is a field the
 * pipeline decided ONCE from the server's verdict — not a regex a caller
 * runs over a message. The distinction that matters most:
 *
 *   - A CONVERSION verdict (`unsupported_image`, `conversion_timeout`,
 *     `conversion_failed`) is deterministic for the same bytes. Re-running
 *     it — through a fallback path, a retry loop, or a sweep — only burns
 *     another lambda and delays the customer's answer. This is what turned
 *     one slow GIF into four server encodes and a "stuck" upload.
 *   - A TRANSPORT failure (`network`, `server_unavailable`) is a moment,
 *     and is retried. An uncoded 500/504 is transport, not a file verdict.
 *
 * Browser-bundled: no server imports, no observability core.
 */

import { getErrorMessage } from '@/lib/shared/errors/error-message'

export type UploadErrorCode =
  /** 422 — sharp could not decode the bytes. Permanent for these bytes. */
  | 'unsupported_image'
  /** 413 — over a size ceiling (`too_large_for_direct` is a routing hint). */
  | 'too_large'
  /** 504 `conversion_timeout` — server budget ran out. Permanent for these bytes. */
  | 'conversion_timeout'
  /** 500 `conversion_failed` — sharp threw on these bytes. Permanent. */
  | 'conversion_failed'
  /** Any other 5xx (incl. uncoded 500/504), 429, 408 — a moment, retried. */
  | 'server_unavailable'
  /** fetch failed / offline / client-side deadline. Retryable. */
  | 'network'
  /** 401 / 403. */
  | 'unauthorized'
  /** `@vercel/blob/client` staging failed after its own retries. */
  | 'blob_stage_failed'
  /** 4xx the pipeline did not classify further. */
  | 'rejected'

const RETRYABLE_CODES: ReadonlySet<UploadErrorCode> = new Set([
  'server_unavailable',
  'network',
  'blob_stage_failed',
])

/**
 * Messages an untyped transport (the `@vercel/blob/client` SDK, a bare
 * `fetch`) uses for a verdict about the FILE — retrying cannot change these.
 * Anything else an SDK throws is treated as a moment.
 */
const PERMANENT_MESSAGE =
  /too large|unsupported|not a supported|invalid content type|not allowed|forbidden|unauthorized/i

export function isPermanentUploadMessage(error: unknown): boolean {
  return PERMANENT_MESSAGE.test(getErrorMessage(error, String(error)))
}

export class UploadError extends Error {
  readonly code: UploadErrorCode
  readonly status: number | undefined
  /** Whether the SAME request could succeed on a later attempt. */
  readonly retryable: boolean

  constructor(
    code: UploadErrorCode,
    message: string,
    options: { status?: number; retryable?: boolean; cause?: unknown } = {}
  ) {
    super(
      message,
      options.cause === undefined ? undefined : { cause: options.cause }
    )
    this.name = 'UploadError'
    this.code = code
    this.status = options.status
    this.retryable = options.retryable ?? RETRYABLE_CODES.has(code)
  }
}

/**
 * A verdict the pipeline has already classified as final for these bytes.
 * Untyped errors are NOT permanent — they are unknowns, and unknowns keep
 * the pre-existing "try once more later" behaviour.
 */
export function isPermanentUploadError(error: unknown): boolean {
  return error instanceof UploadError && !error.retryable
}

/**
 * Whether a staging failure is worth another attempt with the same bytes.
 * Typed errors answer directly; untyped ones (thrown by `@vercel/blob/client`
 * or a bare `fetch`) fall back to a conservative message scan.
 */
export function isRetryableUploadError(error: unknown): boolean {
  if (error instanceof UploadError) {
    return error.retryable
  }
  if (isPermanentUploadMessage(error)) {
    return false
  }
  return /fetch failed|network|timeout|timed out|ECONNRESET|502|503|504|429/i.test(
    getErrorMessage(error, String(error))
  )
}

/** Response body shape every upload/normalize route emits on failure. */
type ErrorBody = { error?: unknown; code?: unknown }

/**
 * Turn a non-OK normalize/import response into a typed error, preserving the
 * server's message (it is written for the customer) and its wire code.
 */
export async function uploadErrorFromResponse(
  response: Response,
  fallbackMessage: string
): Promise<UploadError> {
  let body: ErrorBody = {}
  try {
    body = ((await response.json()) ?? {}) as ErrorBody
  } catch {
    // non-JSON error body (proxy page, empty 5xx) — keep the fallback message
  }
  const message = typeof body.error === 'string' ? body.error : fallbackMessage
  const wireCode = typeof body.code === 'string' ? body.code : undefined
  const status = typeof response.status === 'number' ? response.status : 0

  if (status === 401 || status === 403) {
    return new UploadError('unauthorized', message, { status })
  }
  if (status === 413 || wireCode === 'too_large_for_direct') {
    return new UploadError('too_large', message, { status })
  }
  if (status === 422 || wireCode === 'unsupported_image') {
    return new UploadError('unsupported_image', message, { status })
  }
  // Only the normalizer's OWN timeout is a verdict about the bytes. A bare
  // 504 (HTML or empty body from a proxy in front of the function) is
  // gateway trouble and takes the retryable branch below.
  if (wireCode === 'conversion_timeout') {
    return new UploadError('conversion_timeout', message, { status })
  }
  if (wireCode === 'conversion_failed') {
    // sharp threw on THESE bytes without a typed reason. Almost always
    // deterministic (a libvips decoder fault), and each retry is a full
    // re-encode — so not retried; the caller may still offer the original.
    return new UploadError('conversion_failed', message, { status })
  }
  if (status >= 500 || status === 429 || status === 408) {
    // Any OTHER 5xx — including an uncoded 500 from the framework or the
    // session lookup in front of the handler — is infrastructure, not a
    // statement about the bytes. Retried.
    return new UploadError('server_unavailable', message, { status })
  }
  return new UploadError('rejected', message, { status, retryable: false })
}

/**
 * Wrap whatever a `fetch` threw (offline, DNS, `AbortSignal.timeout`) as a
 * retryable transport failure, keeping the original as `cause`.
 */
export function uploadErrorFromFetchFailure(
  error: unknown,
  label: string
): UploadError {
  if (error instanceof UploadError) {
    return error
  }
  const isTimeout =
    error instanceof Error &&
    (error.name === 'TimeoutError' || error.name === 'AbortError')
  return new UploadError(
    'network',
    isTimeout
      ? `Uploading "${label}" timed out — check your connection and try again`
      : `Could not upload "${label}" — check your connection and try again`,
    { retryable: true, cause: error }
  )
}
